import Draggable from "$root/components/common/Draggable";
import { SidePanelContent, SidePanelHeader, type SidePanelHeaderProps } from "$root/components/common/SidePanelV2";
import { type IconName, Controller, DebouncedSearchInput } from "@mdotm/shadcn/legacy/components";
import { Divider } from "$root/components/Divider";
import { fuzzyFilterSort } from "@mdotm/shadcn/legacy/utils";
import type { ReactNode } from "react";

type DraggableSettingsPanelProps<T> = {
	title: string;
	description?: string;
	onBack?: { icon: IconName; action(): void };
	primaryAction?: SidePanelHeaderProps["primaryAction"];
	items: T[];
	itemKey: (item: T) => string;
	onReorder: (items: T[]) => void;
	children: (item: T, context: { dragIcon: ReactNode; isDragging: boolean }) => ReactNode;
	searchExtract?: (item: T) => string;
	emptyState?: ReactNode;
};

export function DraggableSettingsPanel<T>(props: DraggableSettingsPanelProps<T>) {
	const header = (
		<SidePanelHeader
			title={props.title}
			description={props.description}
			primaryAction={props.primaryAction}
			icon={props.onBack ? { name: props.onBack.icon, onClick: props.onBack.action } : undefined}
		/>
	);

	if (!props.items.length && props.emptyState) {
		return (
			<>
				{header}
				<Divider />
				<SidePanelContent className="flex-1">{props.emptyState}</SidePanelContent>
			</>
		);
	}

	return (
		<div className="flex-1 min-h-0 flex flex-col max-h-dvh h-dvh bg-white pb-4 relative">
			{header}
			<Divider />
			<SidePanelContent className="min-h-0 flex-1 flex flex-col">
				<Controller value="">
					{(search) => {
						const filtered =
							search.value && props.searchExtract
								? fuzzyFilterSort(props.items, search.value, props.searchExtract)
								: props.items;
						return (
							<>
								<DebouncedSearchInput
									type="Body/M/Bold"
									className="mb-4"
									externalQuery=""
									placeholder="Search by keyword..."
									onChange={search.onChange}
								/>
								<div className="min-h-0 flex-1 flex flex-col overflow-y-auto">
									<Draggable
										items={filtered}
										onReorder={props.onReorder}
										itemKey={props.itemKey}
										disabled={Boolean(search.value)}
									>
										{({ data, dragIcon, isDragging }) => props.children(data, { dragIcon, isDragging })}
									</Draggable>
								</div>
							</>
						);
					}}
				</Controller>
			</SidePanelContent>
		</div>
	);
}
